import { Css, FormSectionProps, UseModalHook } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { BasePlanGroupDimensionsModal } from "src/routes/bids/plan-based-bid-package/components/BasePlanGroupDimensionsModal";
import { buildPersonalizationZoneChildSection } from "src/routes/bids/plan-based-bid-package/components/PersonalizationZoneCard";
import { isBaseGroup, PlanBasedBidPackageFormState } from "src/routes/bids/plan-based-bid-package/utils";

type BuildPersonalizationZonesSectionParams = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
  productOfferings: ConfigureBidGroupsStepQuery["bidPackage"]["productOfferings"];
  openModal: UseModalHook["openModal"];
};

/**
 * Builds the "Personalization Zones" `FormSection`, with one `childSection` per zone group.
 *
 * Drag-to-reorder is handled natively by Beam (see `PersonalizationZoneCard.tsx`'s doc comment) —
 * each child's `orderField` is the group's own `order` field, so dragging mutates it directly and
 * `zoneGroups`' `sortBy(order)` reflects the new positions on the next render.
 */
export function buildPersonalizationZonesSection(params: BuildPersonalizationZonesSectionParams): FormSectionProps {
  const { formState, dimensionResults, productOfferings, openModal } = params;
  const zoneGroups = formState.bidPlanGroups.rows
    .filter((g) => isBaseGroup(g.value))
    .sortBy((g) => g.order.value ?? Infinity);

  function openAddPersonalizationZoneModal() {
    openModal({ content: <BasePlanGroupDimensionsModal formState={formState} dimensionResults={dimensionResults} /> });
  }

  return {
    title: "Personalization Zones",
    description:
      "Define zones within the plan that have configurable options. Cost lines populate only for product offerings that include the corresponding zone.",
    actions: [
      {
        icon: "plus",
        label: "Add Personalization Zone",
        variant: "secondary",
        onClick: openAddPersonalizationZoneModal,
      },
    ],
    fields: zoneGroups.isEmpty ? (
      <div css={Css.bgGray100.br8.py3.tac.gray700.$}>No personalization zones added.</div>
    ) : undefined,
    childSections: zoneGroups.isEmpty
      ? undefined
      : zoneGroups.map((group) =>
          buildPersonalizationZoneChildSection({ formState, group, dimensionResults, productOfferings, openModal }),
        ),
  };
}
